<template>
  <base-drawer :show="show" title="Emails" width="520px" @close="onClose">
    <drawer-section title="Customer" icon="icon-single-02">
      <p class="text-white mb-1" v-if="recipientName">
        <router-link class="text-white" :to="{ path: '/customers/details', query: { id: recipientId } }">{{ recipientName }}</router-link>
      </p>
      <small class="text-white d-block">{{ recipientEmail }}</small>
    </drawer-section>

    <drawer-section title="Emails" icon="icon-email-85">
      <loading-panel :loading="loading"></loading-panel>
      <div v-if="!loading && emails.length === 0" class="sms-thread__empty">No emails to this customer yet.</div>
      <div v-for="m in emails" :key="m.id" class="drawer-detail-row text-white align-items-start py-2">
        <div class="mr-3" style="min-width: 0">
          <div class="text-white">{{ emailLabel(m) }}</div>
          <small v-if="m.subject_line" class="d-block text-white">{{ m.subject_line }}</small>
          <small class="d-block text-white">{{ emailSourceLabel(m) }}</small>
          <small v-if="m.message_id || m.transaction_id" class="d-flex flex-wrap text-white" style="gap: 10px">
            <a v-if="m.message_id" href="#" class="text-white" @click.prevent="toggleHistory(m)">{{ openHistory === m.id ? 'Hide history' : 'History' }}</a>
            <router-link v-if="m.transaction_id" class="text-white" :to="{ path: '/purchases/transactions/details', query: { id: m.transaction_id } }">Transaction {{ m.transaction_id }}</router-link>
          </small>
          <small v-if="openHistory === m.id" class="d-block text-white">
            <span v-if="historyLoading">Loading...</span>
            <span v-else-if="history.length === 0">No events recorded</span>
            <span v-else>{{ history.map(h => eventLabel(h) + ' ' + formatTime(h.timestamp)).join(' · ') }}</span>
          </small>
        </div>
        <div class="text-right flex-shrink-0">
          <div class="text-white">{{ formatWhen(m.timestamp) }}</div>
          <span class="tag-chip text-white mt-1" :style="tagChipStyle(statusColor(m.latest_event))">{{ statusLabel(m.latest_event) }}</span>
        </div>
      </div>
    </drawer-section>

    <template #footer>
      <base-button type="secondary" size="sm" @click="onClose">Close</base-button>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
// One customer's emails as a list, newest first, each with its status and an optional
// event history (get-email-log-for-group?recipient_id= and ?message_id=). Read only:
// sending an email needs a transaction, so each email links to its transaction, where
// the Send email drawer lives.
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_COLORS, EMAIL_EVENT_LABELS, emailLabel, emailSourceLabel, eventToStatus } from './emailLogLabels'

const props = defineProps({
  show: { type: Boolean, default: false },
  recipientId: { type: Number, default: null },
  recipient: { type: Object, default: null }
})
const emit = defineEmits<{ (e: 'close'): void }>()
const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/email-log`

const loading = ref(false)
const emails = ref<any[]>([])
const openHistory = ref<number | null>(null)
const history = ref<any[]>([])
const historyLoading = ref(false)

const first = computed(() => (emails.value.length ? emails.value[0] : props.recipient))
const recipientName = computed(() => (first.value ? [first.value.recipient_first_name, first.value.recipient_last_name].filter(Boolean).join(' ') : ''))
const recipientEmail = computed(() => (first.value ? first.value.recipient_email : ''))

function statusLabel(eventType: string) { return EMAIL_STATUS_LABELS[eventToStatus(eventType)] }
function statusColor(eventType: string) { return EMAIL_STATUS_COLORS[eventToStatus(eventType)] }
function eventLabel(h: any) {
  const base = EMAIL_EVENT_LABELS[h.event_type] || h.event_type
  return h.event_type === 'Bounce' && h.bounce_type ? base + ' (' + String(h.bounce_type).toLowerCase() + ')' : base
}
function formatTime(value: string) {
  try { return formatGroupTime(value) } catch (e) { return '' }
}
// Short date (no weekday) so the row's left column keeps room on a phone
function formatWhen(value: string) {
  try { return formatGroupDate(value, { dayOfWeek: false }) + ' ' + formatGroupTime(value) } catch (e) { return String(value).slice(0, 16) }
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function load() {
  if (!props.recipientId) return
  loading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}?recipient_id=${props.recipientId}&count=200`, config)
    emails.value = (response.data.events || []).slice().reverse()
  } catch (e) {
    proxy.$notify({ type: 'danger', message: 'Could not load this customer\'s emails. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

async function toggleHistory(m: any) {
  if (openHistory.value === m.id) {
    openHistory.value = null
    return
  }
  openHistory.value = m.id
  history.value = []
  historyLoading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}?message_id=${encodeURIComponent(m.message_id)}`, config)
    history.value = response.data.history || []
  } catch (e) {
    history.value = []
  } finally {
    historyLoading.value = false
  }
}

function onClose() { emit('close') }
function onEscKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    emails.value = []
    openHistory.value = null
    load()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})
onBeforeUnmount(() => document.removeEventListener('keydown', onEscKey))
</script>
